/*
 * Scent Society membership-status card (on /pages/scent-society).
 *
 * Renders one of five views based on the visitor's loyalty state:
 *   - loading      (initial paint)
 *   - signedout    (not logged in per /api/me)
 *   - earning      (logged in, under 500 points in rolling 365 days)
 *   - member       (logged in, active Scent Society member)
 *   - error        (API call failed)
 *
 * Data source: the portal membership oracle, cross-origin with credentials —
 *   the SAME endpoint scent-society-gate.js uses. The storefront is on New
 *   Customer Accounts, so {{ customer.id }} and /account.js are empty and can't
 *   tell signed-in from signed-out; /api/me can.
 *     GET https://account.elementbrooklyn.com/api/me  ->
 *       { logged_in, scent_society,
 *         customer: { member, member_expires_at,
 *                     points: { rolling_365, lifetime } } }
 *
 * Waste-avoided display uses the page's stated heuristic ("500 points =
 * 500g of packaging avoided"). The EO formula is slightly nuanced
 * (material multipliers), but the page surfaces the round 1:1 framing.
 */

(function () {
  var root = document.querySelector('[data-ss-status]');
  if (!root) return;

  var POINTS_GOAL = 500;
  // Source of truth for login + loyalty. The storefront is on New Customer
  // Accounts, so {{ customer.id }} and /account.js are empty — we must ask the
  // portal (same endpoint scent-society-gate.js uses), cross-origin with
  // credentials. Returns { logged_in, scent_society, customer? } where customer
  // = { member, member_expires_at, points: { rolling_365, lifetime } }.
  var PORTAL_API = 'https://account.elementbrooklyn.com/api/me';

  function show(viewName) {
    var views = root.querySelectorAll('[data-ss-status-view]');
    for (var i = 0; i < views.length; i++) {
      if (views[i].getAttribute('data-ss-status-view') === viewName) {
        views[i].hidden = false;
      } else {
        views[i].hidden = true;
      }
    }
  }

  function setText(selector, value) {
    var el = root.querySelector(selector);
    if (el) el.textContent = value;
  }

  function formatNumber(n) {
    if (typeof n !== 'number' || isNaN(n)) return '0';
    return n.toLocaleString('en-US');
  }

  function formatWaste(points) {
    if (typeof points !== 'number' || isNaN(points) || points <= 0) return '0g';
    if (points >= 1000) return (points / 1000).toFixed(1) + 'kg';
    return points + 'g';
  }

  function formatDate(iso) {
    if (!iso) return '—';
    var d = new Date(iso);
    if (isNaN(d.getTime())) return '—';
    return d.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
  }

  function renderEarning(points) {
    var clamped = Math.min(points || 0, POINTS_GOAL);
    var pct = Math.round((clamped / POINTS_GOAL) * 100);
    setText('[data-ss-status-points]', formatNumber(clamped));
    setText('[data-ss-status-waste]', formatWaste(clamped));
    var fill = root.querySelector('[data-ss-status-fill]');
    // Floor non-zero progress to a visible width; keep true zero empty.
    if (fill) fill.style.width = (clamped > 0 ? Math.max(pct, 3) : 0) + '%';
    var bar = root.querySelector('[data-ss-status-progressbar]');
    if (bar) bar.setAttribute('aria-valuenow', String(clamped));
    show('earning');
  }

  function renderMember(customer) {
    var points = customer.points || {};
    setText('[data-ss-status-lifetime]', formatNumber(points.lifetime || 0));
    // member_since is not provided by the loyalty API; if we ever need it
    // expose it from the backend. For now we hide the field with a dash.
    setText('[data-ss-status-since]', '—');
    if (customer.member_expires_at) {
      var expiry = root.querySelector('[data-ss-status-expiry]');
      if (expiry) expiry.hidden = false;
      setText('[data-ss-status-expiry-date]', formatDate(customer.member_expires_at));
    }
    show('member');
  }

  function renderFromLoyalty(payload) {
    var customer = payload && payload.customer;
    if (!customer) {
      show('error');
      return;
    }
    if (customer.member) {
      renderMember(customer);
    } else {
      var points = (customer.points && customer.points.rolling_365) || 0;
      renderEarning(points);
    }
  }

  function run() {
    show('loading');
    fetch(PORTAL_API, {
      credentials: 'include',
      headers: { Accept: 'application/json' },
    })
      .then(function (r) { return r.ok ? r.json() : null; })
      .then(function (data) {
        shareWithGate(data);
        if (!data || !data.logged_in) {
          show('signedout');
          return;
        }
        // data.customer = { member, member_expires_at, points: {...} }
        renderFromLoyalty(data);
      })
      .catch(function () { show('error'); });
  }

  // This page is where people check (and just changed) their membership, so
  // hand the fresh answer to scent-society-gate.js's cache — the gate skips
  // /api/me while that cache is fresh, and would otherwise show the old state
  // for up to 5 minutes on the next PDP. Same key/shape as the gate.
  function shareWithGate(data) {
    if (!data) return;
    var isMember = !!(data.logged_in && data.scent_society);
    try {
      localStorage.setItem('eb_scent_society_state', JSON.stringify({
        scent_society: isMember,
        expires_at: Date.now() + 5 * 60 * 1000,
      }));
    } catch (e) { /* private mode etc. */ }
    document.body.classList.toggle('scent-society-member', isMember);
    // The body class and cache alone left window.EBMembership (read by
    // recoverShopSignIn, the checkout hop and cart recs) and sale-event.js on
    // the old answer. Same shape as the gate's broadcast(); outside the US the
    // gate isn't loaded, so there's no EBMembership to update.
    if (window.EBMembership) {
      window.EBMembership.pending = false;
      window.EBMembership.member = isMember;
    }
    try {
      document.dispatchEvent(new CustomEvent('eb:membership', {
        detail: { member: isMember, source: 'portal' },
      }));
    } catch (e) { /* ancient browsers */ }
  }

  var retryBtn = root.querySelector('[data-ss-status-retry]');
  if (retryBtn) {
    retryBtn.addEventListener('click', function () { run(); });
  }

  run();
})();
